// Tells a long-lived tab that a newer build is being served, and offers a reload.
//
// The problem it removes is recorded in CLAUDE.md: after a deploy, a stale
// cached index.html produces "Failed to send a request to the Edge Function",
// and the remedy — hard reload — was something you had to already know.
//
// It never reloads on its own. This app holds unsaved work in dialogs
// (ShareholderDialog, LedgerEventDialog), and #76 exists precisely to stop one
// of them closing and losing what was typed. Reloading out from under someone
// mid-entry would be the same defect wearing a different hat.

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { DEV_BUILD_ID, isNewBuild, readBuildId } from '@/utils/buildVersion';

/** Written by the `capledger-build-id` plugin in vite.config.ts. */
export const VERSION_MANIFEST_URL = '/version.json';

export const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Floor between requests. Route changes and tab focus both trigger a check, and
 * a person clicking through the app would otherwise fetch on every navigation.
 */
export const MIN_CHECK_GAP_MS = 30 * 1000;

/**
 * A stable id, so repeated detections replace the notice instead of stacking.
 * Dismissing removes it; the next check puts it back, which is what makes it
 * ignorable while you finish something without being forgettable.
 */
const TOAST_ID = 'capledger-build-update';

interface VersionWatcherProps {
  /**
   * The build compiled into this bundle. Injected so the behaviour can be
   * tested — under vitest `__BUILD_ID__` is the dev sentinel, which would make
   * every test a no-op.
   */
  runningBuildId?: string;
}

export function VersionWatcher({ runningBuildId = __BUILD_ID__ }: VersionWatcherProps) {
  const location = useLocation();
  const lastCheckedAt = useRef(0);

  const check = useCallback(async () => {
    const now = Date.now();
    if (now - lastCheckedAt.current < MIN_CHECK_GAP_MS) return;
    lastCheckedAt.current = now;

    let served: string | null = null;
    try {
      const response = await fetch(VERSION_MANIFEST_URL, { cache: 'no-store' });
      if (!response.ok) return;
      served = readBuildId(await response.json());
    } catch {
      // Offline, a 502 mid-deploy, a body that is not JSON. Every one of these
      // means "learned nothing", never "there is a new build" — announcing on a
      // failed check would reload the user into the build they are already on,
      // and then ask again.
      return;
    }

    if (!isNewBuild(runningBuildId, served)) return;

    toast('A new version of CapLedger is available.', {
      id: TOAST_ID,
      duration: Infinity,
      description: 'Reload to pick it up. Anything you are part-way through will be lost.',
      action: {
        label: 'Reload',
        onClick: () => window.location.reload(),
      },
    });
  }, [runningBuildId]);

  useEffect(() => {
    // `npm run dev` serves no manifest, and the sentinel would never compare
    // equal to anything. Poll nothing rather than 404 every five minutes.
    if (runningBuildId === DEV_BUILD_ID) return;

    void check();

    const interval = setInterval(() => void check(), CHECK_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') void check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // `location.pathname` is a dependency so navigating re-checks; the throttle
    // above keeps that from turning into a request per click.
  }, [check, runningBuildId, location.pathname]);

  return null;
}
